import { type FormEvent, useState } from "react";
import { Form } from "react-bootstrap";
import { HEYGEN_API_KEYS_URL, type HeyGenStatus } from "shared";
import { parseError } from "../common/error";
import { Button } from "../components/Button";
import { useHeyGen, useHeyGenActions } from "./useIntegrations";

const accessLabel = (access: HeyGenStatus["access"]) => {
	switch (access) {
		case "read_only":
			return "Read only";
		case "read_write":
			return "Read and write";
		default:
			return "Unknown";
	}
};

function ConnectForm(props: { onCancel?: () => void }) {
	const { connect } = useHeyGenActions();
	const [apiKey, setApiKey] = useState("");
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		const value = apiKey.trim();
		if (!value || connect.isPending) {
			return;
		}
		setError(null);
		// Drop the key from component state as soon as it is sent.
		setApiKey("");
		try {
			await connect.mutateAsync(value);
			props.onCancel?.();
		} catch (err) {
			setError(parseError(err) ?? "Could not connect HeyGen. Try again.");
		}
	};

	return (
		<form className="heygen-connect-form" onSubmit={handleSubmit}>
			<ol className="heygen-connect-steps">
				<li>
					Open{" "}
					<a href={HEYGEN_API_KEYS_URL} target="_blank" rel="noreferrer">
						HeyGen API keys
					</a>{" "}
					and create a new key with <strong>read-only</strong> access.
				</li>
				<li>Paste it below. We store it encrypted and never show it again.</li>
			</ol>
			<Form.Control
				type="password"
				className="sentry-mask"
				value={apiKey}
				onChange={(e) => setApiKey(e.target.value)}
				placeholder="Paste your HeyGen API key"
				aria-label="HeyGen API key"
				autoComplete="off"
				spellCheck={false}
				data-1p-ignore
				data-lpignore="true"
				disabled={connect.isPending}
			/>
			{error && (
				<p className="heygen-connect-error" role="alert">
					{error}
				</p>
			)}
			<div className="heygen-connect-actions">
				{props.onCancel && (
					<Button variant="secondary" onClick={props.onCancel}>
						Cancel
					</Button>
				)}
				<Button
					type="submit"
					full={!props.onCancel}
					loading={connect.isPending}
					disabled={apiKey.trim().length === 0}
				>
					Connect
				</Button>
			</div>
		</form>
	);
}

export const HeyGenAppsCard = () => {
	const { status } = useHeyGen();
	const { disconnect } = useHeyGenActions();
	const [showForm, setShowForm] = useState(false);
	const data = status.data;
	const needsAttention = data.connected && data.state === "invalid";

	return (
		/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: baseline */
		<div className="app-connect" aria-label="HeyGen">
			<div className="app-connect-header">
				<div className="app-connect-metadata">
					<span className="app-connect-heygen-logo" aria-hidden>
						HG
					</span>
					<div>
						<div>HeyGen</div>
						<div>Media library</div>
					</div>
				</div>
				{data.connected && (
					<Form.Check
						type="switch"
						disabled={disconnect.isPending}
						onClick={(e) => {
							e.preventDefault();
							if (
								window.confirm(
									"Disconnect HeyGen? Videos you already imported stay in your media library.",
								)
							) {
								disconnect.mutate();
							}
						}}
						checked
						readOnly
						className="app-connect-switch"
					/>
				)}
			</div>
			<div className="app-connect-spacer" />

			{!data.connected ? (
				showForm ? (
					<ConnectForm onCancel={() => setShowForm(false)} />
				) : (
					<Button onClick={() => setShowForm(true)} full>
						Connect
					</Button>
				)
			) : (
				<>
					{needsAttention && (
						<div className="app-connect-oauth-error" role="alert">
							<div className="app-connect-oauth-error__title">
								Needs attention
							</div>
							<p className="app-connect-oauth-error__message">
								HeyGen stopped accepting this API key. It may have been revoked
								or expired. Paste a new key to keep importing videos.
							</p>
						</div>
					)}
					{data.accountLabel && (
						<div className="app-connect-page-selection">
							Account
							<span className="active">{data.accountLabel}</span>
						</div>
					)}
					<div className="app-connect-page-selection">
						Access
						<span className="active">{accessLabel(data.access)}</span>
					</div>
					{data.access === "read_write" && (
						<p className="heygen-connect-hint">
							This key can also create videos and spend HeyGen credits. Moxie
							only reads videos, so a read-only key is safer.
						</p>
					)}
					{data.keyExpiresAt && (
						<div className="app-connect-page-selection">
							Key expires
							<span className="active">
								{new Date(data.keyExpiresAt).toLocaleDateString()}
								{data.expiringSoon ? " (soon)" : ""}
							</span>
						</div>
					)}
					{showForm ? (
						<ConnectForm onCancel={() => setShowForm(false)} />
					) : (
						<Button
							variant={needsAttention ? "primary" : "secondary"}
							onClick={() => setShowForm(true)}
							full
						>
							{needsAttention ? "Reconnect" : "Replace API key"}
						</Button>
					)}
				</>
			)}
		</div>
	);
};
